/**
 * useLocaleAutoPopulate
 *
 * On first sign-in, silently fetches the device's locale + region via
 * expo-localization and persists it to the user's Supabase profile if
 * `country` or `locale` is still null. Does nothing on subsequent logins
 * or when the profile is already populated, so users are free to edit
 * these values manually later without being overwritten.
 */

import { useEffect, useRef } from 'react';
import * as Localization from 'expo-localization';

import { useSupabaseAuth } from '@/lib/supabase';
import { authenticatedFetch } from '@/lib/services/AuthenticatedFetchService';

export function useLocaleAutoPopulate(): void {
  const { session } = useSupabaseAuth();
  const hasAttemptedRef = useRef<string | null>(null);

  useEffect(() => {
    const userId = session?.user?.id ?? null;
    if (!userId) return;
    if (hasAttemptedRef.current === userId) return;
    hasAttemptedRef.current = userId;

    async function populate() {
      try {
        const response = await authenticatedFetch.fetch('/api/user-profile');
        if (!response.ok) return;

        const { profile } = (await response.json()) as {
          profile: { country?: string | null; locale?: string | null };
        };

        if (profile.country && profile.locale) return;

        const [loc] = Localization.getLocales();
        if (!loc) return;

        const country = profile.country ?? loc.regionCode ?? null;
        const locale = profile.locale ?? loc.languageTag ?? null;

        if (!country && !locale) return;

        await authenticatedFetch.fetch('/api/user-profile', {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...(profile.country ? {} : { country }),
            ...(profile.locale ? {} : { locale }),
          }),
        });
      } catch (err) {
        console.warn('[useLocaleAutoPopulate] Failed to populate locale:', err);
      }
    }

    populate();
  }, [session?.user?.id]);
}
